import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { onMiniCartOpen, useCart } from "@/lib/cart/store";
import { calcPricing, formatPrice } from "@/lib/cart/pricing";
import { useShippingConfig } from "@/lib/admin/settings";
import { CartLineRow } from "./CartLineRow";

export function MiniCart() {
  const [open, setOpen] = useState(false);
  const lines = useCart();
  const shippingConfig = useShippingConfig();
  const pricing = calcPricing(lines, null, null, shippingConfig);

  useEffect(() => onMiniCartOpen(() => setOpen(true)), []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="flex w-full max-w-md flex-col border-l border-black/10 bg-white p-0"
      >
        <VisuallyHidden>
          <SheetTitle>Your bag</SheetTitle>
        </VisuallyHidden>
        <div
          className="flex items-center justify-between border-b border-black/10 px-6 py-5"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          <h2 className="text-lg tracking-[0.24em] text-black">YOUR BAG</h2>
          <span className="text-[11px] uppercase tracking-[0.22em] text-black/50">
            {lines.reduce((s, l) => s + l.quantity, 0)} item
            {lines.reduce((s, l) => s + l.quantity, 0) === 1 ? "" : "s"}
          </span>
        </div>

        {lines.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag className="h-8 w-8 text-black/30" strokeWidth={1.25} />
            <p className="text-lg text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your bag is empty
            </p>
            <p className="max-w-xs text-sm text-black/60">
              Discover the CB16 Maison collection and start composing your olfactive wardrobe.
            </p>
            <Link
              to="/fragrances"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center border border-black bg-black px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B]"
            >
              Shop Fragrances
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 divide-y divide-black/10 overflow-y-auto px-6">
              {lines.map((line) => (
                <CartLineRow
                  key={`${line.productId}:${line.variantId}`}
                  line={line}
                  variant="mini"
                  onNavigate={() => setOpen(false)}
                />
              ))}
            </div>
            <div className="border-t border-black/10 px-6 py-5">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.22em] text-black/60">
                  Subtotal
                </span>
                <span className="text-base font-medium tabular-nums text-black">
                  {formatPrice(pricing.subtotal)}
                </span>
              </div>
              {pricing.freeShippingRemaining > 0 && (
                <p className="mb-4 text-[11px] uppercase tracking-[0.22em] text-black/50">
                  {formatPrice(pricing.freeShippingRemaining)} away from free Standard Delivery
                </p>
              )}
              <div className="flex flex-col gap-2">
                <Link
                  to="/cart"
                  onClick={() => setOpen(false)}
                  className="inline-flex w-full items-center justify-center border border-black bg-black px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B]"
                >
                  View Bag & Checkout
                </Link>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="inline-flex w-full items-center justify-center px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-black/70 transition hover:text-[#C6A15B]"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
